// Professional Document Analysis Viewer
// Features: Stats overview, interactive topic cards, glossary with search, copy-to-clipboard questions

import React, { useState, useMemo, useCallback } from 'react';
import {
  FileText,
  Clock,
  BookOpen,
  HelpCircle,
  CheckCircle,
  Copy,
  Download,
  Search,
  Tag,
  List,
  MessageSquare,
  GraduationCap,
  FileImage,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { DocumentAnalysis } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface AnalysisViewerProps {
  analysis: DocumentAnalysis;
  className?: string;
}

const complexityConfig = {
  beginner: {
    label: 'Beginner',
    colour: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dotColour: 'bg-emerald-500',
  },
  intermediate: {
    label: 'Intermediate',
    colour: 'bg-amber-100 text-amber-800 border-amber-200',
    dotColour: 'bg-amber-500',
  },
  advanced: {
    label: 'Advanced',
    colour: 'bg-red-100 text-red-800 border-red-200',
    dotColour: 'bg-red-500',
  },
} as const;

const importanceOrder: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const importanceBorderColour: Record<string, string> = {
  high: 'border-l-blue-500',
  medium: 'border-l-amber-500',
  low: 'border-l-slate-400',
};

const importanceBadgeColour: Record<string, string> = {
  high: 'bg-blue-100 text-blue-800 border-blue-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

export function AnalysisViewer({ analysis, className = '' }: AnalysisViewerProps) {
  const [termSearchQuery, setTermSearchQuery] = useState('');
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  const complexity = complexityConfig[analysis.complexity];

  const sortedTopics = useMemo(() => {
    return [...analysis.keyTopics].sort(
      (a, b) => (importanceOrder[a.importance] ?? 2) - (importanceOrder[b.importance] ?? 2)
    );
  }, [analysis.keyTopics]);

  const filteredTerminology = useMemo(() => {
    if (!termSearchQuery.trim()) return analysis.terminology;
    const query = termSearchQuery.toLowerCase();
    return analysis.terminology.filter(
      (item) =>
        item.term.toLowerCase().includes(query) ||
        item.definition.toLowerCase().includes(query)
    );
  }, [analysis.terminology, termSearchQuery]);

  const showTermSearch = analysis.terminology.length > 5;

  const handleCopyQuestion = useCallback((question: string) => {
    navigator.clipboard.writeText(question).then(() => {
      toast.success('Question copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy question');
    });
  }, []);

  const handleExportPdf = useCallback(() => {
    exportToPdf('analyze', analysis);
  }, [exportToPdf, analysis]);

  const handleExportDocx = useCallback(() => {
    exportToDocx('analyze', analysis);
  }, [exportToDocx, analysis]);

  return (
    <TooltipProvider>
      <ScrollArea className={cn('h-full', className)}>
        <div className="space-y-6 p-1">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2 min-w-0 flex-1">
              <h2 className="text-xl font-bold text-foreground leading-tight">
                {analysis.title}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn('text-xs font-medium', complexity.colour)}
                >
                  <span className={cn('mr-1.5 h-2 w-2 rounded-full', complexity.dotColour)} />
                  {complexity.label}
                </Badge>
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Clock className="mr-1 h-3 w-3" />
                  {analysis.estimatedReadTime}
                </Badge>
              </div>
            </div>

            {/* Export dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isExporting}
                  className="shrink-0"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPdf}>
                  <FileImage className="mr-2 h-4 w-4" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportDocx}>
                  <FileText className="mr-2 h-4 w-4" />
                  Export as Word (.docx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Stats overview bar */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="border-0 bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40">
                  <Tag className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-lg font-bold leading-none">{analysis.keyTopics.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Topics</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                  <List className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-lg font-bold leading-none">{analysis.mainPoints.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Main Points</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
                  <BookOpen className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-lg font-bold leading-none">{analysis.terminology.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Terms</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/40">
                  <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-lg font-bold leading-none">{analysis.suggestedQuestions.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Questions</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <Card className="overflow-hidden border-0 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/60 dark:to-slate-800/40">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <FileText className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed text-foreground/90">
                {analysis.summary}
              </p>
            </CardContent>
          </Card>

          {/* Key Topics */}
          {analysis.keyTopics.length > 0 && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Tag className="h-4 w-4" />
                Key Topics
              </h3>
              <div className="space-y-2">
                {sortedTopics.map((topic, index) => (
                  <Card
                    key={index}
                    className={cn(
                      'border-l-4 transition-shadow hover:shadow-md',
                      importanceBorderColour[topic.importance]
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1 min-w-0 flex-1">
                          <p className="font-semibold text-sm text-foreground">
                            {topic.topic}
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {topic.description}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            'shrink-0 text-xs capitalize',
                            importanceBadgeColour[topic.importance]
                          )}
                        >
                          {topic.importance}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Main Points */}
          {analysis.mainPoints.length > 0 && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <CheckCircle className="h-4 w-4" />
                Main Points
              </h3>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {analysis.mainPoints.map((point, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          {index + 1}
                        </span>
                        <div className="flex items-start gap-2 pt-0.5 min-w-0">
                          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                          <p className="text-sm leading-relaxed text-foreground/90">
                            {point}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Key Terminology */}
          {analysis.terminology.length > 0 && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <GraduationCap className="h-4 w-4" />
                Key Terminology
              </h3>

              {showTermSearch && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search terms..."
                    value={termSearchQuery}
                    onChange={(e) => setTermSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              )}

              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {filteredTerminology.map((item, index) => (
                      <div
                        key={index}
                        className={cn(
                          'flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4',
                          index % 2 === 0
                            ? 'bg-transparent'
                            : 'bg-slate-50/50 dark:bg-slate-900/30'
                        )}
                      >
                        <span className="shrink-0 text-sm font-semibold text-foreground sm:w-40">
                          {item.term}
                        </span>
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          {item.definition}
                        </span>
                      </div>
                    ))}
                    {filteredTerminology.length === 0 && termSearchQuery && (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No terms matching &ldquo;{termSearchQuery}&rdquo;
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Suggested Questions */}
          {analysis.suggestedQuestions.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                  <HelpCircle className="h-4 w-4" />
                  Suggested Questions
                </h3>
                <p className="text-xs text-muted-foreground">
                  These questions can help deepen your understanding
                </p>
              </div>
              <div className="space-y-2">
                {analysis.suggestedQuestions.map((question, index) => (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleCopyQuestion(question)}
                        className={cn(
                          'group flex w-full items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3 text-left transition-all hover:bg-blue-100/60 hover:shadow-sm dark:border-blue-900/40 dark:bg-blue-950/20 dark:hover:bg-blue-950/40'
                        )}
                      >
                        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                        <span className="flex-1 text-sm leading-relaxed text-foreground/90">
                          {question}
                        </span>
                        <Copy className="mt-0.5 h-4 w-4 shrink-0 text-blue-400 opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Click to copy</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          {/* Bottom spacing */}
          <div className="h-4" />
        </div>
      </ScrollArea>
    </TooltipProvider>
  );
}

export default AnalysisViewer;
